import React, { useEffect, useState } from "react";
import { Handle, Position, useEdges } from "@xyflow/react";
import {
  Box,
  Center,
  Image,
  Input,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  VStack,
  HStack,
  Flex,
} from "@chakra-ui/react";
import imageMap from "../../utils/assetImageMap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { setSelectedNode } from "../../services/state/Process-modular/processModular";
import { NodePropertiesModal } from "../../components/NodePropertiesModal/NodePropertiesModal";
import { AddNodeIOModal } from "../../components/NodePropertiesModal/AddNodeIOModal";

export interface CustomNodeProps {
  id: string;
  data: {
    id: string;
    label: string;
    image: number;
    shape: string;
    onLabelChange: (id: string, newLabel: string) => void;
    nodeIoDetails: any[];
    nodeStatus?: string;
    memoryComponents?: Array<{
      id: string;
      label: string;
      type: string;
    }>;
    inputPorts?: Array<{
      id: string;
      label: string;
      type: string;
    }>;
    outputPorts?: Array<{
      id: string;
      label: string;
      type: string;
    }>;
  };
  style?: React.CSSProperties;
}

const CustomisedNode: React.FC<CustomNodeProps> = ({ data }) => {
  const [label, setLabel] = useState(data.label);
  const [isEditing, setIsEditing] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch: AppDispatch = useDispatch();

  const edges = useEdges();

  // Get selected node and connection state from Redux
  const selectedNode = useSelector(
    (state: RootState) => state.processModeler.selectedNode
  );
  const connect = useSelector(
    (state: RootState) => state.processModeler.isConnect
  );
  const theme = useSelector(
    (state: RootState) => state.processModeler.selectedTheme
  );

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(event.target.value);
  };

  const handleBlur = () => {
    if (label !== data.label) {
      data.onLabelChange(data.id, label);
    }
    setIsEditing(false);
  };

  const getStatusColor = (status: string | undefined | null) => {
    const normalizedStatus = status?.toLowerCase() || "";
    switch (normalizedStatus) {
      case "completed":
        return { borderColor: "#7652F7", bgColor: "#2D3748", color: "white" };
      case "started":
        return {
          borderColor: "green.400",
          bgColor: "green.400",
          color: "white",
        };
      case "failed":
        return { borderColor: "red.400", bgColor: "red.400", color: "white" };
      default:
        return { borderColor: "#4A5568", bgColor: "#2D3748", color: "white" };
    }
  };

  const getShapeStyles = (shape: string) => {
    const baseSize = 100;
    const smallSize = 80;

    switch (shape.toLowerCase()) {
      case "circle":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderRadius: "md",
          clipPath: "none",
        };
      case "square":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderRadius: "0px",
          clipPath: "none",
        };
      case "diamond":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderRadius: "md",
          clipPath: "none",
        };
      case "hexagon":
        return {
          width: `${baseSize}px`,
          height: `${baseSize * 0.866}px`, // height = width * √3/2 for regular hexagon
          borderRadius: "0px",
          clipPath:
            "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
        };
      case "triangle":
        return {
          width: `${smallSize}px`,
          height: `${smallSize}px`,
          borderRadius: "4px",
          clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
        };
      case "pentagon":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderRadius: "4px",
          clipPath: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
        };
      case "octagon":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderRadius: "0px",
          clipPath:
            "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)",
        };
      case "parallelogram":
        return {
          width: `${baseSize}px`,
          height: `${baseSize * 0.7}px`,
          borderRadius: "4px",
          clipPath: "polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)",
        };
      case "trapezoid":
        return {
          width: `${baseSize}px`,
          height: `${baseSize * 0.7}px`,
          borderRadius: "4px",
          clipPath: "polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)",
        };
      case "left-rounded-rectangle":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderTopLeftRadius: "30px",
          borderBottomLeftRadius: "30px",
          borderTopRightRadius: "0px",
          borderBottomRightRadius: "0px",
          clipPath: "none",
        };
      case "right-rounded-rectangle":
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderTopLeftRadius: "0px",
          borderBottomLeftRadius: "0px",
          borderTopRightRadius: "30px",
          borderBottomRightRadius: "30px",
          clipPath: "none",
        };
      default: // rectangle
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          borderRadius: "md",
          clipPath: "none",
        };
    }
  };

  const getContentLayout = (shape: string) => {
    const isSmallShape = ["circle", "diamond", "triangle"].includes(
      shape.toLowerCase()
    );

    return {
      iconSize: "48px",
      fontSize: "12px",
      padding: 3,
      spacing: 2,
      showDescription: true,
    };
  };

  const { borderColor, bgColor, color } = getStatusColor(data.nodeStatus);

  const isNodeConnected = (nodeId: string) => {
    return edges.some(
      (edge) => edge.source === nodeId || edge.target === nodeId
    );
  };

  const toggleNodeSelection = () => {
    dispatch(setSelectedNode(data.id));
  };

  useEffect(() => {
    setLabel(data.label);
  }, [data.label]);

  // Render input/output connection points
  const renderConnectionPoint = (
    id: string,
    label: string,
    position: "top" | "bottom",
    isInput: boolean = true
  ) => (
    <Box
      key={id}
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="center"
      minW="60px"
    >
      <Box
        w="8px"
        h="8px"
        bg="gray.400"
        borderRadius="full"
        border="2px solid"
        borderColor="gray.600"
        position="relative"
      />
      <Text
        fontSize="10px"
        color="gray.400"
        mt={position === "top" ? 1 : 0}
        mb={position === "bottom" ? 1 : 0}
        textAlign="center"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </Box>
  );

  return (
    <Box position="relative">
      {/* ALL ORIGINAL HANDLES - Keep these for backward compatibility but make them invisible */}
      {connect && (
        <>
          {/* Original handles from first file - Made invisible */}
          <Handle
            id="left-source-handle"
            type="source"
            position={Position.Left}
            isConnectable={true}
            style={{
              background: "#7652F7", // subtle gray color
              width: "0.5px",
              height: "0.5px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
          <Handle
            id="left-target-handle"
            type="target"
            position={Position.Left}
            isConnectable={true}
            style={{
              background: "#7652F7", // subtle gray color
              width: "0.5px",
              height: "0.5px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
              padding: 1,
            }}
          />
          <Handle
            id="right-target-handle"
            type="target"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: "#888", // subtle gray color
              border: "1px solid #aaa", // light border for visibility
              width: "1px", // slightly larger for easier interaction
              height: "1px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
          <Handle
            id="right-source-handle"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: "#888", // subtle gray color
              border: "1px solid #aaa", // light border for visibility
              width: "1px", // slightly larger for easier interaction
              height: "1px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
          <Handle
            id="top-target-handle"
            type="target"
            position={Position.Top}
            isConnectable={true}
            style={{
              background: "#888", // subtle gray color
              border: "1px solid #aaa", // light border for visibility
              width: "1px", // slightly larger for easier interaction
              height: "1px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
          <Handle
            id="top-source-handle"
            type="source"
            position={Position.Top}
            isConnectable={true}
            style={{
              background: "#888", // subtle gray color
              border: "1px solid #aaa", // light border for visibility
              width: "1px", // slightly larger for easier interaction
              height: "1px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
          <Handle
            id="bottom-target-handle"
            type="target"
            position={Position.Bottom}
            isConnectable={true}
            style={{
              background: "#888", // subtle gray color
              border: "1px solid #aaa", // light border for visibility
              width: "1px", // slightly larger for easier interaction
              height: "1px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
          <Handle
            id="bottom-source-handle"
            type="source"
            position={Position.Bottom}
            isConnectable={true}
            style={{
              background: "#888", // subtle gray color
              border: "1px solid #aaa", // light border for visibility
              width: "1px", // slightly larger for easier interaction
              height: "1px",
              borderRadius: "50%", // circular shape
              opacity: 0.3, // slightly faded
              zIndex: 100,
            }}
          />
        </>
      )}

      <VStack spacing={3} align="center">
        {/* Top input connections */}
        {data.inputPorts && data.inputPorts.length > 0 && (
          <HStack spacing={4}>
            {data.inputPorts.map((port) =>
              renderConnectionPoint(port.id, port.label, "top", true)
            )}
          </HStack>
        )}

        {/* Main node body */}
        <Box
          onClick={toggleNodeSelection}
          onDoubleClick={onOpen}
          {...getShapeStyles(data.shape)}
          bg={bgColor}
          border="2px solid"
          borderColor={borderColor}
          boxShadow={
            selectedNode === data.id
              ? "0px 0px 8px 2px rgba(118, 82, 247, 0.6)"
              : "0px 2px 8px rgba(0,0,0,0.3)"
          }
          transition="all 0.2s ease"
          cursor="pointer"
          _hover={{
            boxShadow: "0px 4px 12px rgba(0,0,0,0.4)",
          }}
          p={getContentLayout(data.shape).padding}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
          overflow="hidden"
        >
          <VStack spacing={0.5}>
            {/* Node icon */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              // mt={4}
            >
              <Image
                src={imageMap[data.image as keyof typeof imageMap]}
                alt="Node image"
                width="40px"
                height="40px"
                objectFit="contain" // or "cover" depending on your needs
                boxSize="40px" // optional shorthand
                filter={theme === "light" ? "none" : "brightness(1.2)"}
              />
            </Box>

            {/* Node label */}
            <Box>
              <Input
                value={label}
                onChange={handleLabelChange}
                onBlur={handleBlur}
                size="xs"
                textAlign="center"
                border="none"
                bg="transparent"
                color={color}
                fontSize={getContentLayout(data.shape).fontSize}
                fontWeight="medium"
                p={0}
                minH="auto"
                h="auto"
                _focus={{
                  border: "1px solid",
                  borderColor: "brand.300",
                  bg: "white",
                  color: "black",
                }}
              />
            </Box>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default CustomisedNode;
